// Home.js — the landing page: every page in the app, grouped as the sidebar groups them.
//
// The list is derived from PAGE_CONFIG rather than kept here, because a second hand-maintained
// copy drifts: this page had ten of the twenty-five pages on it, missing the whole Admin section
// and most of what had been added to Coaching. Adding a page to PAGE_CONFIG now puts it here too.
//
// What's shown is filtered by the same `can()` the sidebar and the route guard use, so nobody is
// offered a page that would refuse them on arrival.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, List } from 'lucide-react';
import { TbBar, TbDivider, TbLogo, TbIconBtn } from './Toolbar';
import { pageIcons, NAV_PAGES, SECTIONS, EMPTY_SECTIONS_SHOWN } from './Sidebar';
import { useAuth } from './AuthContext';
import './Home.css';

// A line on what each page is for. Keyed by page id, and entirely optional — a page with no
// entry still appears, just without the subtitle, so a new page can never be silently dropped.
const DESCRIPTIONS = {
  '/data-entry': 'Session dates, RPE and squad availability',
  '/day-to-day-summaries': 'The daily wellness report, the daily training report and the post match report',
  '/daily-training-report': 'Post-session training load and drill breakdown, split by session type',
  '/post-match-report': 'Match-day physical output, with subjective and medical views',
  '/opposition-physicals': 'Any team’s running from SkillCorner, by match or across a season',
  '/training-drill-calculator': 'Plan and cost drills against load targets',
  '/individual-player-load-tracking-report': 'Per-player load trends over time',
  '/load-budget-chronic-load-overview': 'Acute:chronic workload ratio tracking',
  '/wellness-monitoring-report': 'Player-reported wellness and readiness',
  '/wellness-longitudinal-tracking': 'Wellness, hydration and completion over time',
  '/heart-rate': 'Heart-rate distribution across sessions',
  '/match-performance': 'Match physicals over time and within a match, the metric explorer and match minutes',
  '/nutrition': 'Bodyweights and hydration, hydration, and skinfolds and bodyweight',
  '/rehab': 'A player\u2019s Rehab days and weeks against their match load',
  '/set-pieces': 'Set-piece routines drawn on the pitch',
  '/set-pieces-positioning': 'Where teams stand at corners and free kicks, from 360 frames',
  '/set-piece-summary': 'Every corner in a competition: where it goes, from which side, and what comes of it',
  '/game-model-review': 'Attacking and defending principles with match trends',
  '/tactical-xml': 'StatsBomb and SkillCorner XML generator',
  '/style-of-play': 'Team tactical phase breakdown against the competition',
  '/team-analysis': 'Style & outcomes, chance creation and chance prevention for any club',
  '/coaching-post-match-report': 'Five-page match report: general stats, attack, defence, transition and individuals',
  '/movement': 'Off-ball run types and forward runs into the final third',
  '/playground': 'SkillCorner tracking, 3D body pose and StatsBomb ghosted tracking on a pitch',
  '/ball-in-play': 'Ball-in-play time by competition and season',
  '/metric-maker': 'Build metrics from event data and watch the clips',
  '/data-pull-performance': 'Pull Catapult, Firstbeat and Orreco data into the warehouse',
  '/data-pull-football': 'Pull Impect, SkillCorner, StatsBomb and Opta data, and build ghosted tracking',
  '/catapult': 'Pull Catapult GPS into the data warehouse',
  '/catapult-raw': 'Match intensity in 1, 5, 10 and 15 minute blocks, computed from the 10 Hz stream',
  '/firstbeat-raw': 'Heart-rate zone blocks at 1, 5, 10 and 15 minutes, computed from the 1 Hz series',
  '/firstbeat': 'Pull Firstbeat heart-rate sessions into the warehouse',
  '/orreco': 'Pull Orreco wellness into the warehouse',
  '/impect': 'Pull IMPECT master, match and season data into ClickHouse',
  '/opta': 'Pull Opta (Stats Perform SDAPI) feeds into ClickHouse',
  '/statsbomb': 'Pull StatsBomb competitions, matches, events, 360 frames and lineups into ClickHouse',
  '/scoreboard-creator': 'Burn a clock and scoreboard into Sportscode clips',
  '/line-up-creator': "Download any Pro League club's squad as the club's Keynote slide",
  '/video-size-reducer': 'Shrink a MOV or MP4 on this computer, with no upload, and save it to Downloads',
  '/match-video-uploader': 'Upload a match\'s TV and wide-angle footage, tag the halves and build the XML timeline',
  '/storage-testing': 'iDrive e2 playground: manage buckets, pull a clip out of a long video fast, download anything',
  '/chat': 'Message anyone on the platform, one to one or in group chats',
  '/permissions': 'User accounts and per-page access',
  '/idp': 'Individual Development Plans and their day-by-day calendar',
  '/game-model': 'The club Game Model the IDPs are built from',
};

// Sections that share a column on Home, top to bottom. These are short, so they sit under Data Entry
// rather than each taking a column of its own; any section not named here gets its own.
const SHARED_COLUMNS = [['Data Entry', 'Player Development', 'Squad Planning', 'Academy']];

function Home() {
  const [collapsed, setCollapsed] = useState(false);
  const { can, user } = useAuth();

  const sections = useMemo(() => {
    const allowed = NAV_PAGES.filter((p) => p.section !== 'top' && can(p.id));
    return SECTIONS
      .map((title) => ({ title, pages: allowed.filter((p) => p.section === title) }))
      .filter((s) => s.pages.length > 0 || EMPTY_SECTIONS_SHOWN.has(s.title));
  }, [can]);

  const total = sections.reduce((n, s) => n + s.pages.length, 0);

  // Sections grouped into columns, in sidebar order, each shared column placed where its first
  // section would have been.
  const columns = useMemo(() => {
    const out = [];
    const placed = new Set();
    for (const s of sections) {
      if (placed.has(s.title)) continue;
      const group = SHARED_COLUMNS.find((g) => g.includes(s.title));
      const col = group ? group.map((t) => sections.find((x) => x.title === t)).filter(Boolean) : [s];
      col.forEach((x) => placed.add(x.title));
      out.push(col);
    }
    return out;
  }, [sections]);

  return (
    <div className="home-container">
      <TbBar className="home-toolbar">
        <TbLogo />
        <TbDivider />
        <span className="home-greeting">
          {user && user.displayName ? user.displayName : 'Welcome'}
        </span>
        <span className="home-count">{total} {total === 1 ? 'page' : 'pages'}</span>
        <div style={{ flex: 1 }} />
        <TbIconBtn
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? 'Grid view' : 'Compact view'}
        >
          {collapsed ? <LayoutGrid size={14} /> : <List size={14} />}
        </TbIconBtn>
      </TbBar>

      <div className={`home-sections ${collapsed ? 'collapsed' : ''}`}>
        {total === 0 && (
          <div className="home-empty-note">
            You don’t have access to any pages yet — ask an administrator to grant some.
          </div>
        )}
        {columns.map((col) => (
          <div key={col.map((x) => x.title).join('|')} className={`home-column ${col.length > 1 ? 'home-column-stacked' : ''}`}>
          {col.map((section) => (
          <div key={section.title} className="home-section">
            <div className="home-section-header">
              <h2 className="home-section-title">{section.title}</h2>
              <div className="home-section-line" />
            </div>
            <div className={`home-grid ${collapsed ? 'collapsed' : ''}`}>
              {section.pages.length === 0 && <p className="home-section-none">Nothing here yet</p>}
              {section.pages.map((page) => (
                <Link key={page.id} to={page.id} className="home-card">
                  <div className={`home-card-content ${collapsed ? 'collapsed' : ''}`}>
                    <span className="home-card-icon">{pageIcons[page.icon]}</span>
                    <div className="home-text-wrapper">
                      <h2 className="home-page-title">{page.name}</h2>
                      {!collapsed && DESCRIPTIONS[page.id] && (
                        <p className="home-description">{DESCRIPTIONS[page.id]}</p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Home;
